import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import PageHeading from 'components/heading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import ExportModal from 'components/modals/export';
import DateRangeBar from 'components/date-range-bar';
import RefreshButton from 'components/refresh-button';
import useQuery from 'utils/hooks/useQuery';
import useDateRange from 'utils/hooks/useDateRange';
import QUERY from 'utils/query';
import { TIPS_TABS, tipsThresholdDate } from 'utils/constants';
import { SUMMARY } from 'state/summary/type';
import usePayment from 'state/payment/hooks/usePayment';
import TipSummary from './tipSummary';
import TipsDetails from './tipDetails';
import TipBanner from './components/TipBanner';
import useTipsFilters from './hooks/useTipsFilters';
import { TipsTab } from './types';
import { TIPS_SUBHEADING } from './constants';

const Tips = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useQuery(location?.search);

  const {
    payment,
    toClearTipsSummaryExport,
    toFetchTipsSummaryCSV,
    toFetchTipsDetailsCSV,
    toClearTipsExport,
    toFetchTipsSummary,
    toFetchTipsList,
  } = usePayment();

  const {
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    comparisonFrom: prevFrom,
    comparisonTo: prevTo,
    dateFromISO,
    dateToISO,
  } = useDateRange({ syncToUrl: true, defaultRange: '7days' });

  const {
    filters,
    exportFilters,
    paymentTypeArray,
    storeIdArray,
    amountOption,
    firstAmount,
    secondAmount,
    searchString,
  } = useTipsFilters({ dateFromISO, dateToISO });

  const [showModal, setShowModal] = useState(false);
  const [summaryType, setSummaryType] = useState('employee');
  const [intervalTypes, setIntervalTypes] = useState<SUMMARY.IIntervalType[]>(
    []
  );
  const [activeTab, setActiveTab] = useState<string>(
    urlParams.get('tab')?.toLowerCase() === TipsTab.DETAILS.toLowerCase()
      ? TipsTab.DETAILS
      : TipsTab.SUMMARY
  );
  const [withInDateRange, setWithInDateRange] = useState(true);

  const { url, error } = payment;

  useEffect(() => {
    const queryTabParam = urlParams.get('tab')?.toLowerCase();
    const tabFromUrl = TIPS_TABS.find(
      (t) => t.label.toLowerCase() === queryTabParam
    )?.label;

    if (!tabFromUrl) {
      navigate({
        search: QUERY.update(location.search, { tab: 'summary' }),
      });
    } else if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    setWithInDateRange(new Date(dateFromISO) < tipsThresholdDate);
  }, [dateFromISO]);

  const handleTabChange = (value: string) => {
    navigate({
      search: QUERY.update(location.search, {
        tab: value.toLowerCase(),
      }),
    });
    setActiveTab(value);
  };

  const handleRefresh = () => {
    if (activeTab === TipsTab.SUMMARY) {
      const fromFallback = moment()
        .subtract(7, 'day')
        .startOf('day')
        .toISOString();
      const toFallback = moment().endOf('day').toISOString();
      toFetchTipsSummary({
        start_date: dateFromISO || fromFallback,
        end_date: dateToISO || toFallback,
        summary_type: summaryType,
        interval_types: intervalTypes.join(),
        search_string: searchString,
        prev_start_date: prevFrom,
        prev_end_date: prevTo,
      });
    } else {
      toFetchTipsList({
        key: urlParams.get('key') ?? undefined,
        start_date: dateFromISO || undefined,
        end_date: dateToISO || undefined,
        amount_1: firstAmount,
        amount_2: secondAmount,
        condition: amountOption || undefined,
        payment_type:
          paymentTypeArray.length > 0 ? paymentTypeArray.join(',') : undefined,
        search_string: searchString,
      });
    }
  };

  return (
    <div className="rounded-lg">
      <PageHeading
        heading="Tips"
        subHeading={TIPS_SUBHEADING}
        className="mb-7"
        headingAppendRight={
          !payment.isLoading ? (
            <RefreshButton onRefresh={handleRefresh} activeTab={activeTab} />
          ) : undefined
        }
      />

      <DateRangeBar
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        comparisonFrom={prevFrom}
        comparisonTo={prevTo}
        className="mb-5"
      />

      {withInDateRange && <TipBanner isDismissable />}

      <Tabs value={activeTab} onValueChange={handleTabChange} id="tips-tabs">
        <TabsList className="w-full justify-start gap-0 rounded-none border-b border-gray-blue-10 bg-transparent p-0 px-8 -mx-8 h-auto mb-5">
          {TIPS_TABS.map((item) => (
            <TabsTrigger
              key={item.label}
              value={item.label}
              id={`${item.label.toLowerCase()}-tab`}
              data-testid={`${item.label.toLowerCase()}-tab`}
              className="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-blue-50 bg-transparent shadow-none data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:shadow-none"
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={TipsTab.SUMMARY} className="mt-0">
          <TipSummary
            activeTab={activeTab}
            onExport={() => setShowModal(true)}
            summaryType={summaryType}
            toSetSummaryType={setSummaryType}
            intervalTypes={intervalTypes}
            toSetIntervalTypes={setIntervalTypes}
          />
        </TabsContent>

        <TabsContent value={TipsTab.DETAILS} className="mt-0">
          <TipsDetails
            filterConfig={filters}
            onExport={() => setShowModal(true)}
            activeTab={activeTab}
            dateFrom={dateFromISO}
            dateTo={dateToISO}
            firstAmount={firstAmount}
            secondAmount={secondAmount}
            amountOption={amountOption}
            paymentTypeArray={paymentTypeArray}
            storeIdArray={storeIdArray}
            searchString={searchString}
            queryParams={location?.search}
            setActiveTab={setActiveTab}
          />
        </TabsContent>
      </Tabs>

      {showModal && (
        <ExportModal
          showModal={showModal}
          setShowModal={setShowModal}
          config={exportFilters}
          heading={
            activeTab === TipsTab.SUMMARY ? 'Tip Summary' : 'Tip Details'
          }
          onExport={
            activeTab === TipsTab.SUMMARY
              ? toFetchTipsSummaryCSV
              : toFetchTipsDetailsCSV
          }
          reportUrl={url}
          label="Tips"
          errorMessage={error.addUrl?.message ?? undefined}
          toClearExport={
            activeTab === TipsTab.SUMMARY
              ? toClearTipsSummaryExport
              : toClearTipsExport
          }
          isTipsExport={true}
        />
      )}
    </div>
  );
};

export default Tips;
